import { useEffect, useRef, useState } from 'react'
import { useFocusTrap } from '../hooks/useFocusTrap'

const CATEGORIES: { label: string; emojis: string[] }[] = [
  { label: 'Recent', emojis: [] }, // populated from localStorage
  { label: 'Smileys', emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😋','😛','😜','🤪','😝','🤗','🤔','🫣','🤭','🤫','😶','😏','😒','🙄','😬','😮‍💨','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐'] },
  { label: 'People', emojis: ['👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🫰','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','🫵','👍','👎','👊','✊','🤛','🤜','👏','🙌','🫶','👐','🤲','🤝','🙏'] },
  { label: 'Nature', emojis: ['🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐻‍❄️','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🐔','🐧','🐦','🦅','🦆','🦉','🐝','🐛','🦋','🐌','🪱','🌸','🌺','🌻','🌹','🌷','🌱','🌲','🌳','🍀'] },
  { label: 'Food', emojis: ['🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🥑','🍕','🍔','🍟','🌭','🥪','🌮','🌯','🥗','🍝','🍜','🍛','🍣','🍱','🍩','🍪','🎂','🍰','☕','🍵','🧃','🍺','🍷','🥂','🍾'] },
  { label: 'Activities', emojis: ['⚽','🏀','🏈','⚾','🥎','🎾','🏐','🏉','🥏','🎱','🏓','🏸','🥅','⛳','🎯','🏋️','🧗','🚴','🏄','🏊','🤽','🧘','🎪','🎨','🎬','🎤','🎧','🎮','🎲','🧩','♟️'] },
  { label: 'Travel', emojis: ['🚗','🚕','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🛻','🚚','🚛','🚜','✈️','🚀','🛸','🚁','🛶','⛵','🚤','🛥️','🏠','🏢','🏗️','🏭','🏰','🗼','🗽','⛪','🕌','🌍','🌎','🌏','🗺️'] },
  { label: 'Objects', emojis: ['📱','💻','⌨️','🖥️','🖨️','📷','📹','🎥','📞','☎️','📺','📻','🔔','📣','📢','💡','🔦','🏮','📖','📚','✏️','🖊️','📝','📁','📂','📅','📆','📌','📎','🔗','📐','✂️','🗑️','🔒','🔑'] },
  { label: 'Symbols', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❤️‍🔥','💯','✅','❌','⭕','❗','❓','💲','🔥','⚡','💫','🌟','✨','💥','💢','💤','🕐','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪'] },
]

const RECENT_KEY = 'slash-recent-emoji'
const MAX_RECENT = 16

function getRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
  } catch {
    return []
  }
}

function addRecent(emoji: string) {
  const recent = getRecent().filter((e) => e !== emoji)
  recent.unshift(emoji)
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

interface EmojiPickerProps {
  onSelect: (emoji: string) => void
  onClose: () => void
}

export default function EmojiPicker({ onSelect, onClose }: EmojiPickerProps) {
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  useFocusTrap(ref)

  // Populate recent
  const categories = [...CATEGORIES]
  categories[0] = { ...categories[0], emojis: getRecent() }

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const handleSelect = (emoji: string) => {
    addRecent(emoji)
    onSelect(emoji)
  }

  // Flatten + filter for search
  const allEmojis = search
    ? categories.flatMap((c) => c.emojis)
        .filter((e, i, arr) => arr.indexOf(e) === i)
        .filter((e) => e.includes(search) || getEmojiName(e).includes(search.toLowerCase()))
    : null

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      className="absolute bottom-full left-0 mb-2 flex h-[320px] w-[340px] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg"
    >
      {/* Search */}
      <div className="shrink-0 border-b border-zinc-100 px-3 py-2">
        <input
          autoFocus
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search emoji..."
          className="w-full rounded-lg bg-zinc-50 px-2.5 py-1.5 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        />
      </div>

      {/* Category tabs */}
      {!search && (
        <div className="flex shrink-0 gap-0.5 border-b border-zinc-100 px-2 py-1">
          {categories.map((cat, i) => (
            <button
              key={cat.label}
              onClick={() => setActiveCategory(i)}
              className={`rounded-md px-1.5 py-0.5 text-[10px] ${
                activeCategory === i
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Emoji grid */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {search ? (
          <div className="flex flex-wrap gap-0.5">
            {(allEmojis ?? []).map((emoji) => (
              <EmojiButton key={emoji} emoji={emoji} onClick={handleSelect} />
            ))}
            {allEmojis?.length === 0 && (
              <span className="px-2 py-4 text-xs text-zinc-400">No results</span>
            )}
          </div>
        ) : (
          <>
            {categories[activeCategory].emojis.length === 0 && activeCategory === 0 ? (
              <span className="px-2 py-4 text-xs text-zinc-400">No recent emoji</span>
            ) : (
              <div className="flex flex-wrap gap-0.5">
                {categories[activeCategory].emojis.map((emoji) => (
                  <EmojiButton key={emoji} emoji={emoji} onClick={handleSelect} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Basic emoji name lookup for search — maps common emoji to searchable keywords.
// Uses the Unicode name heuristic: code point to a rough label.
const emojiNameCache = new Map<string, string>()
function getEmojiName(emoji: string): string {
  const cached = emojiNameCache.get(emoji)
  if (cached !== undefined) return cached
  // Attempt to use Intl.Segmenter if available (modern browsers)
  try {
    const name = emoji.toLowerCase()
    emojiNameCache.set(emoji, name)
    return name
  } catch {
    emojiNameCache.set(emoji, '')
    return ''
  }
}

function EmojiButton({ emoji, onClick }: { emoji: string; onClick: (emoji: string) => void }) {
  return (
    <button
      onClick={() => onClick(emoji)}
      className="flex h-8 w-8 items-center justify-center rounded-md text-lg hover:bg-zinc-100"
    >
      {emoji}
    </button>
  )
}
